import { Player, PlayerRef } from '@remotion/player'
import React, { useCallback, useEffect, useRef, useState, memo } from 'react'
import { MyCompositionHtml } from './myCompositionHtml'
import RenderPosterComponent from './renderPoster'
import { renderStill } from '@remotion/renderer'
import { usePlayer } from '@src/context/playerContext'
import { useGlobalContext } from '@src/context/globalContext'
// import { MyComp } from '@src/remotionRoot'

// Create a custom hook for the playerRef
export const usePlayerRef = () => {
  const playerRef = useRef<PlayerRef>(null);
  return playerRef;
};

type gifProps = {
  htmlData: any
  duration: number
  jsonData: any
  transitionDurationInFrames: number
  transitionAnimationProp: any
  isDownload: boolean
  scriptDetails: any
  burstImagesTransitionAnimationArray: any
  videoStyle: string,
  aiAvatar: boolean,
  widthVW?: number,
  heightVH?: number,
  isWorkspace?: boolean
}

// Memoized Player component to prevent re-renders
const MemoizedPlayer = memo(({ 
  playerRef, 
  durationInFrames, 
  inputProps, 
  videoPlayerWidth, 
  videoPlayerHeight, 
  style 
}: any) => {
  return (
    <Player
      ref={playerRef}
      fps={30}
      durationInFrames={durationInFrames}
      component={MyCompositionHtml as unknown as React.ComponentType<Record<string, unknown>>}
      autoPlay={false}
      inputProps={inputProps}
      controls={true}
      compositionWidth={videoPlayerWidth}
      compositionHeight={videoPlayerHeight}
      numberOfSharedAudioTags={10}
      style={style}
      allowFullscreen={true} // Prevent unnecessary re-renders from fullscreen changes
      clickToPlay={false} // Disable click to play to avoid re-renders
    />
  );
}, (prevProps, nextProps) => {
  // Deep comparison for specific props that shouldn't trigger re-renders when seeking
  if (prevProps.durationInFrames !== nextProps.durationInFrames) return false;
  if (prevProps.videoPlayerWidth !== nextProps.videoPlayerWidth) return false;
  if (prevProps.videoPlayerHeight !== nextProps.videoPlayerHeight) return false;
  
  // For style and inputProps, only do a shallow comparison
  // as we don't want to re-render for just style/inputProps changes during seeking
  return true;
});

// Add display name to fix linter error
MemoizedPlayer.displayName = 'MemoizedPlayer';

// Assuming you want to use the Remotion Player for previewing the video
export const VideoPlayer = ({
  htmlData,
  duration,
  jsonData,
  transitionDurationInFrames,
  transitionAnimationProp,
  isDownload,
  scriptDetails,
  burstImagesTransitionAnimationArray,
  videoStyle,
  aiAvatar,
  widthVW = 0.3,
  heightVH = 0.65,
  isWorkspace = false
}: gifProps) => {
  const {UserDetail: [userDetails, ]} = useGlobalContext()
  const playerRef = usePlayer();
  const [isInitialized, setIsInitialized] = useState(false);

  const maxVideoWidth = window.innerWidth * widthVW;
  const maxVideoHeight = window.innerHeight * heightVH;

  const videoPlayerWidth = isDownload ? jsonData.width : Math.round(jsonData.width / 1);
  const videoPlayerHeight = isDownload ? jsonData.height : Math.round(jsonData.height / 1);

  // Calculate scaled dimensions
  const aspectRatio = videoPlayerWidth / videoPlayerHeight;
  let scaledWidth = videoPlayerWidth;
  let scaledHeight = videoPlayerHeight;

  if (scaledWidth > maxVideoWidth) {
    scaledWidth = maxVideoWidth;
    scaledHeight = maxVideoWidth / aspectRatio;
  }

  if (scaledHeight > maxVideoHeight) {
    scaledHeight = maxVideoHeight;
    scaledWidth = maxVideoHeight * aspectRatio;
  }

  // Use useMemo for input props and style to prevent re-renders
  const memoizedInputProps = React.useMemo(
    () => ({
      htmlData,
      jsonData: jsonData,
      scriptDetails: {
        ...scriptDetails,
        ...userDetails?.purpose
      },
      width: videoPlayerWidth,
      height: videoPlayerHeight,
      transitionDurationInFrames,
      transitionAnimationProp,
      isDownload,
      burstImagesTransitionAnimationArray,
      videoStyle,
      aiAvatar
    }),
    [
      htmlData,
      jsonData,
      scriptDetails,
      userDetails?.purpose,
      videoPlayerWidth,
      videoPlayerHeight,
      transitionDurationInFrames,
      transitionAnimationProp,
      isDownload,
      burstImagesTransitionAnimationArray,
      videoStyle,
      aiAvatar
    ]
  );

  const calculatedDuration = React.useMemo(() => {
    return duration <= 0 
      ? 400 
      : memoizedInputProps?.scriptDetails?.topicIntroduction
        ? duration + (memoizedInputProps?.scriptDetails?.data?.length * 60) 
        : duration;
  }, [duration, memoizedInputProps?.scriptDetails?.topicIntroduction, memoizedInputProps?.scriptDetails?.data?.length]);

  const playerStyle = React.useMemo(() => ({
    width: `${isWorkspace ? scaledWidth*0.6 : scaledWidth}px`,
    height: `${isWorkspace ? scaledHeight*0.6 : scaledHeight}px`,
  }), [scaledWidth, scaledHeight, isWorkspace]);

  // Effect to handle player initialization
  useEffect(() => {
    if (playerRef.current && !isInitialized) {
      console.log('VideoPlayer - Player initialized');
      setIsInitialized(true);
    }
  }, [playerRef.current, isInitialized]);

  return (
    <MemoizedPlayer
      playerRef={playerRef}
      durationInFrames={calculatedDuration}
      inputProps={memoizedInputProps}
      videoPlayerWidth={videoPlayerWidth}
      videoPlayerHeight={videoPlayerHeight}
      style={playerStyle}
    />
  );
};
